import React, { Fragment } from "react";
import { lazyWithRetry } from "../../core/lazy_with_retry";
import { useI18n } from "../../lib/i18n/i18n_provider";

import { OfferingDetailsMultiPass } from "../template_offering_details_multipass";
import { useMDotAnalytics } from "./mdot_analytics";
import {
  dayPassGeneralInfoEN,
  dayPassGeneralInfoHE,
  partnerConfig,
  testimonialsEN,
  testimonialsHE,
} from "./mdot_partner_config";

const Theme = lazyWithRetry(() => import("./mdot_theme"));

const faqsEN = [
  {
    question: "What are your operating hours?",
    answer:
      "We are open for daily on-demand booking on Sunday-Thursday, 8:30 am to 5:30 pm. We would be happy to have you as our guest during these operating hours.",
  },
  {
    question: "Does MULTI PASS allow me to stay after 5:30 pm?",
    answer:
      "The terms that apply to daily users also apply to those who have purchased several daily entries (Multi Pass). Consequently, in this case too, it is not permitted to remain in the complex beyond our operating hours (before 8:30 am or after 5:30 pm).",
  },
  {
    question: "What does my Multi Pass include?",
    answer:
      "The Multi Pass includes entry into the complex during operating hours on Sunday -Thursday, for as many days as you like, until the end of the current calendar month. It gives you access to the common spaces, a fast and secure Internet connection and free use of the fully accessorized kitchen. Access to open space workstations is subject to availability.",
  },
  {
    question: "Is purchasing a Multi Pass a good investment?",
    answer:
      "The Multi Pass is an exceptionally wise investment! It’s perfect for anyone planning to use our facilities more than 8 times in the current calendar month. Towards the end of the month, we recommend purchasing a Daily Pass, and to upgrade to Multi Pass at the beginning of the following month.",
  },
  {
    question: "Can I get credit or a refund if I haven’t use all the entries?",
    answer:
      "No refunds or credit are issued for unused entries and access cannot be rolled over to the next month.",
  },
  {
    question:
      "What commitments does the Multi Pass include? Is it automatically renewed at the end of the month?",
    answer:
      "As we like to say, maximum flexibility is our policy! And besides, we like to see you choose us each time anew :). The Multi Pass expires automatically at the end of each calendar month.",
  },
  {
    question: "Can I share my Multi Pass with another user?",
    answer: "The Multi Pass is personal and non-transferable.",
  },
  {
    question: "Will I have the same workstation every time I come?",
    answer:
      "A Multi Pass does not guarantee the same workstation every time. Occupation of a workstation in the open space is on a daily basis subject to availability, and the workstation should be cleared of all possessions at the end of the day.",
  },
  {
    question: "Where can I park?",
    answer: `What could be better than parking in a reserved spot in the building? For that option, contact us by email at info@m-dot.co.il and book the number of parking spaces you need in advance. Parking spaces are subject to availability and an additional fee. 
    Alternately, free parking is available within a short walking distance, in an unpaved parking lot next to the Hermozo banquet hall (entry via HaZriya Street). `,
  },
  {
    question: "Where can I eat?",
    answer:
      "Food can be ordered in from a wide variety of restaurants in Modiin, or you can avail yourself of the dining room in the building which offers a delicious kosher lunch for an additional fee. You can always grab a snack from the snack dispenser in the lobby, or bring food from home, which you can store in the refrigerator and heat up in the microwave in the main kitchen.",
  },
  {
    question: "Is it possible to make phone calls in the open space?",
    answer:
      "The open space is designated as a “quiet zone” where phone calls are not allowed. If you need to, you can use one of the telephone booths in the complex.",
  },
  {
    question: "Can I invite guests?",
    answer:
      "Daily entry does not include guests. If you wish to invite guests, you can book one of our meeting rooms for a separate fee, subject to availability.",
  },
  {
    question: "Is there a printer available for use?",
    answer:
      "A printer is available for your use in the administration area. It can be used for a separate fee.",
  },
];

const faqsHE = [
  {
    question: "מהן שעות הפעילות שלכם?",
    answer:
      "המתחם פתוח להזמנות יומיות בימים א'-ה' בין השעות 08:30 - 17:30. נשמח לארח אותך אצלנו במסגרת שעות פעילות אלה.",
  },
  {
    question: "אם רכשתי כמה כניסות אני יכול להישאר יותר?",
    answer:
      "התקנון של אורח יומי חל גם על מי שרכש מספר כניסות יומיות, ולכן גם במקרה זה לא ניתן להישאר במתחם מחוץ לשעות הפעילות (לפני 08:30 ואחרי 17:30)",
  },
  {
    question: "מה כולל המנוי היומי?",
    answer: `המנוי היומי כולל כניסה למתחם בשעות הפעילות שלו בכל יום עד סוף החודש הקלנדרי שבו נעשה המנוי. המנוי מאפשר  גישה לחללים הציבוריים, אינטרנט מהיר ומאובטח ושימוש חופשי במטבח. הישיבה בעמדת עבודה באופן ספייס היא ע"ב מקום פנוי.`,
  },
  {
    question: "מתי זה משתלם לי?",
    answer:
      "ה - MULTI PASS משתלם (מאד!) למי שמתכנן להגיע עד סוף החודש הקלנדרי הנוכחי מעל 8 פעמים. לקראת סוף החודש מומלץ לרכוש כניסות יומיות ולשדרג למנוי בתחילת החודש הבא",
  },
  {
    question: "ואם לא השתמשתי בכל הכניסות אני אקבל זיכוי?",
    answer:
      "לא ינתנו החזר ו/או זיכוי בגין כניסות שלא נוצלו ולא ניתן יהיה להעביר זכות שימוש לחודש הבא",
  },
  {
    question: "איזו התחייבות יש במנוי היומי? הוא מתחדש אוטומטית?",
    answer:
      "כמו שאנחנו אוהבות כאן - הגמישות היא מקסימאלית! (וחוץ מזה אנחנו נהנות לראות אתכם בוחרים בנו כל פעם מחדש :) ). המנוי היומי מסתיים אוטומטית בסוף החודש .",
  },
  {
    question: "האם אפשר לחלוק את המנוי היומי עם משתמש נוסף?",
    answer: "המנוי היומי הינו אישי ואינו ניתן להעברה",
  },
  {
    question: "האם תהיה לי עמדת עבודה קבועה?",
    answer:
      "מנוי יומי אינו מבטיח עמדת עבודה קבועה. הישיבה באופן ספייס היא על בסיס מקום פנוי, ובסוף היום יש לפנות אותה מכל חפץ",
  },
  {
    question: "איפה חונים?",
    answer: `ניתן לחנות ברחוב או במגרש חניה חינמי הנמצא במרחק 5 דקות הליכה (סמוך לאולם האירועים "הרמוזו"). בהזמנה מראש, ע"ב מקום פנוי ובתוספת תשלום, ניתן לשריין חניה שמורה בבניין`,
  },
  {
    question: "איפה אוכלים?",
    answer:
      "ניתן להזמין משלוחים ממגון מסעדות במודיעין או לעלות לחדר האוכל שבבניין המציע בופה צהריים עשיר וכשר (בתשלום נפרד). בנוסף, תוכלו להביא איתכם אוכל ולאחסן במקרר שבמטבח הראשי  או לסגור את הפינה עם חטיפים מהמכונה שבלובי",
  },
  {
    question: "האם אפשר לקיים שיחות טלפון באופן ספייס?",
    answer: `אזור האופן ספייס מוגדר כ"אזור עבודה שקט" שבו לא ניתן לקיים שיחות טלפון. במקרה הצורך תוכלו לעשות שימוש באחד מתאי הטלפון העומדים לרשותכם במתחם`,
  },
  {
    question: "האם ניתן להזמין אורחים?",
    answer:
      "כניסה יומית לאופן ספייס אינה כוללת אורחים. במידה ותרצו להזמין אורחים תוכלו לשריין את אחד מחדרי הישיבות שלנו, על בסיס מקום פנוי ובתוספת תשלום.",
  },
  {
    question: "האם יש מדפסת זמינה?",
    answer:
      "בפינת האדמיניסטרציה במתחם עומדת לרשותך מכונת צילום /מדפסת. ניתן לעשות בה שימוש בתשלום נפרד",
  },
];

export function OfferingMDotMultiPass() {
  useMDotAnalytics();
  const { locale } = useI18n();

  return (
    <Fragment>
      <Theme />
      <OfferingDetailsMultiPass
        showAddress={false}
        showDirections
        partnerConfig={partnerConfig}
        data={{
          testimonials:
            locale.baseLanguageTag === "en" ? testimonialsEN : testimonialsHE,
          faqs: locale.baseLanguageTag === "en" ? faqsEN : faqsHE,
          generalInfo:
            locale.baseLanguageTag === "en"
              ? dayPassGeneralInfoEN
              : dayPassGeneralInfoHE,
        }}
      />
    </Fragment>
  );
}
